// Medidas de Desempeño de la Gerencia (MPM) — NIIF 18 Pilar 2.
// Contabilidad → Reportes → Medidas de la Gerencia (MPM).
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Plus, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { Modal, Campo, Switch, fieldCls } from '@/features/inventario/shared';
import { Imprimible, PrintButton } from '@/components/print/DocPrint';
import { BuscadorCuenta } from '@/components/ui/BuscadorCuenta';
import { Cuenta, cargarPlan, rpc, usd, hoy, inicioMes } from './shared';

const SUBTOTALES: Record<string, string> = {
  utilidad_bruta: 'Utilidad bruta', utilidad_operativa: 'Utilidad operativa',
  antes_fin_impuestos: 'Utilidad antes de financiación e impuestos',
  antes_impuestos: 'Utilidad antes de impuestos', resultado_periodo: 'Resultado del período',
};
const NATS: Record<string, string> = {
  depreciacion: 'Depreciación', amortizacion: 'Amortización', beneficios_empleados: 'Beneficios a empleados',
  deterioro: 'Deterioro', inventarios: 'Inventarios', otros: 'Otros',
};

interface Ajuste { id: string; orden: number; concepto: string; signo: number; tipo_origen: string; cuenta_id: string | null; naturaleza_gasto: string | null; monto_manual: number | null }
interface Mpm { id: string; etiqueta: string; descripcion: string | null; por_que_util: string | null; subtotal_base: string; activo: boolean; ajustes: Ajuste[] }

export function MPM() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { data: lista = [], isFetching } = useQuery<Mpm[]>({ queryKey: ['cont-mpm'], queryFn: () => rpc<Mpm[]>('cont_mpm_lista') });
  const { data: cuentas = [] } = useQuery<Cuenta[]>({ queryKey: ['cont-plan'], queryFn: cargarPlan });
  const mov = useMemo(() => cuentas.filter((c) => c.es_movimiento && ['ingreso', 'costo', 'gasto'].includes(c.tipo)), [cuentas]);

  const [selId, setSelId] = useState<string>('');
  const sel = lista.find((m) => m.id === selId) || null;
  const [desde, setDesde] = useState(inicioMes); const [hasta, setHasta] = useState(hoy);

  // --- edición de la definición de la MPM ---
  const vacio = { etiqueta: '', descripcion: '', por_que_util: '', subtotal_base: 'utilidad_operativa', activo: true };
  const [defOpen, setDefOpen] = useState(false); const [defEdit, setDefEdit] = useState<string | null>(null);
  const [def, setDef] = useState(vacio);
  const abrirDef = (m?: Mpm) => {
    if (m) { setDefEdit(m.id); setDef({ etiqueta: m.etiqueta, descripcion: m.descripcion ?? '', por_que_util: m.por_que_util ?? '', subtotal_base: m.subtotal_base, activo: m.activo }); }
    else { setDefEdit(null); setDef(vacio); }
    setDefOpen(true);
  };
  const guardarDef = useMutation({
    mutationFn: () => rpc<string>('cont_mpm_guardar', { _id: defEdit, _etiqueta: def.etiqueta, _descripcion: def.descripcion || null, _por_que_util: def.por_que_util || null, _subtotal_base: def.subtotal_base, _activo: def.activo }),
    onSuccess: (id) => { toast.success('MPM guardada'); setDefOpen(false); setSelId((s) => s || id); void qc.invalidateQueries({ queryKey: ['cont-mpm'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const eliminarMpm = useMutation({
    mutationFn: (id: string) => rpc('cont_mpm_eliminar', { _id: id }),
    onSuccess: () => { toast.success('MPM eliminada'); setSelId(''); void qc.invalidateQueries({ queryKey: ['cont-mpm'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  // --- edición de ajustes ---
  const ajVacio = { concepto: '', signo: 1, tipo_origen: 'naturaleza', cuenta_id: '', naturaleza_gasto: 'depreciacion', monto_manual: '', orden: 0 };
  const [ajOpen, setAjOpen] = useState(false); const [ajEdit, setAjEdit] = useState<string | null>(null);
  const [aj, setAj] = useState(ajVacio);
  const abrirAj = (a?: Ajuste) => {
    if (a) { setAjEdit(a.id); setAj({ concepto: a.concepto, signo: a.signo, tipo_origen: a.tipo_origen, cuenta_id: a.cuenta_id ?? '', naturaleza_gasto: a.naturaleza_gasto ?? 'depreciacion', monto_manual: a.monto_manual != null ? String(a.monto_manual) : '', orden: a.orden }); }
    else { setAjEdit(null); setAj({ ...ajVacio, orden: (sel?.ajustes.length || 0) + 1 }); }
    setAjOpen(true);
  };
  const guardarAj = useMutation({
    mutationFn: () => rpc('cont_mpm_ajuste_guardar', {
      _id: ajEdit, _mpm_id: selId, _orden: aj.orden, _concepto: aj.concepto, _signo: Number(aj.signo), _tipo_origen: aj.tipo_origen,
      _cuenta_id: aj.tipo_origen === 'cuenta' ? (aj.cuenta_id || null) : null,
      _naturaleza_gasto: aj.tipo_origen === 'naturaleza' ? aj.naturaleza_gasto : null,
      _monto_manual: aj.tipo_origen === 'manual' ? Number(aj.monto_manual || 0) : null,
    }),
    onSuccess: () => { toast.success('Ajuste guardado'); setAjOpen(false); void qc.invalidateQueries({ queryKey: ['cont-mpm'] }); void qc.invalidateQueries({ queryKey: ['cont-mpm-conc'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const eliminarAj = useMutation({
    mutationFn: (id: string) => rpc('cont_mpm_ajuste_eliminar', { _id: id }),
    onSuccess: () => { toast.success('Ajuste eliminado'); void qc.invalidateQueries({ queryKey: ['cont-mpm'] }); void qc.invalidateQueries({ queryKey: ['cont-mpm-conc'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  // --- conciliación ---
  const { data: conc, isFetching: concLoad } = useQuery<any>({
    queryKey: ['cont-mpm-conc', selId, desde, hasta, sel?.ajustes.length], enabled: !!selId,
    queryFn: () => rpc<any>('cont_mpm_conciliacion', { _mpm_id: selId, _desde: desde, _hasta: hasta }),
  });

  const nombreCuenta = (id: string | null) => { const c = mov.find((x) => x.id === id); return c ? `${c.codigo} · ${c.nombre}` : '—'; };

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <div className="flex items-center justify-between gap-2">
        <h1 className="text-xl font-semibold">Medidas de la Gerencia · MPM (NIIF 18)</h1>
        <Button onClick={() => abrirDef()}><Plus className="w-4 h-4 mr-1" /> Nueva MPM</Button>
      </div>
      <p className="text-sm text-muted-foreground">NIIF 18: las métricas de la gerencia (EBITDA ajustado, utilidad ajustada, etc.) se definen, concilian con el subtotal NIIF más comparable y quedan dentro del alcance de auditoría.</p>

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4">
        {/* lista */}
        <Card><CardHeader className="pb-2"><CardTitle className="text-base">Medidas ({lista.length})</CardTitle></CardHeader>
          <CardContent className="space-y-1">
            {isFetching && <div className="py-4 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-primary" /></div>}
            {lista.length === 0 && !isFetching && <p className="text-sm text-muted-foreground py-4">Aún no hay MPM definidas.</p>}
            {lista.map((m) => (
              <button key={m.id} onClick={() => setSelId(m.id)} className={`w-full text-left rounded-md px-3 py-2 text-sm ${selId === m.id ? 'bg-primary/10 font-medium' : 'hover:bg-muted'}`}>
                {m.etiqueta}{!m.activo && <span className="ml-1 text-xs text-muted-foreground">(inactiva)</span>}
                <div className="text-xs text-muted-foreground">base: {SUBTOTALES[m.subtotal_base]}</div>
              </button>
            ))}
          </CardContent>
        </Card>

        {/* detalle */}
        {sel ? (
          <div className="space-y-4">
            <Card><CardHeader className="pb-2 flex-row items-center justify-between">
              <CardTitle className="text-base">{sel.etiqueta}</CardTitle>
              <div className="flex gap-1">
                <button className="inline-flex w-8 h-8 items-center justify-center rounded-md hover:bg-muted" title="Editar definición" onClick={() => abrirDef(sel)}><Pencil className="w-4 h-4" /></button>
                <button className="inline-flex w-8 h-8 items-center justify-center rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20" title="Eliminar MPM" onClick={async () => { if (await dlg.confirmar(`¿Eliminar la MPM "${sel.etiqueta}"?`, { peligro: true })) eliminarMpm.mutate(sel.id); }}><Trash2 className="w-4 h-4" /></button>
              </div></CardHeader>
              <CardContent className="text-sm space-y-1">
                {sel.descripcion && <p><span className="text-muted-foreground">Descripción:</span> {sel.descripcion}</p>}
                {sel.por_que_util && <p><span className="text-muted-foreground">Por qué es útil:</span> {sel.por_que_util}</p>}
                <p><span className="text-muted-foreground">Subtotal NIIF base:</span> {SUBTOTALES[sel.subtotal_base]}</p>
              </CardContent>
            </Card>

            {/* ajustes */}
            <Card><CardHeader className="pb-2 flex-row items-center justify-between">
              <CardTitle className="text-base">Ajustes de conciliación</CardTitle>
              <Button variant="outline" size="sm" onClick={() => abrirAj()}><Plus className="w-4 h-4 mr-1" /> Ajuste</Button>
            </CardHeader>
              <CardContent className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b text-left text-xs text-muted-foreground"><th className="py-2 pr-3">#</th><th className="py-2 pr-3">Concepto</th><th className="py-2 pr-3">Signo</th><th className="py-2 pr-3">Origen</th><th className="py-2"></th></tr></thead>
                  <tbody>
                    {sel.ajustes.length === 0 && <tr><td colSpan={5} className="py-6 text-center text-muted-foreground">Sin ajustes — la MPM igualaría al subtotal base.</td></tr>}
                    {sel.ajustes.map((a) => (
                      <tr key={a.id} className="border-b last:border-0">
                        <td className="py-1.5 pr-3 text-muted-foreground">{a.orden}</td>
                        <td className="py-1.5 pr-3">{a.concepto}</td>
                        <td className="py-1.5 pr-3">{a.signo > 0 ? '+' : '−'}</td>
                        <td className="py-1.5 pr-3 text-muted-foreground">
                          {a.tipo_origen === 'cuenta' && nombreCuenta(a.cuenta_id)}
                          {a.tipo_origen === 'naturaleza' && `Naturaleza: ${NATS[a.naturaleza_gasto || ''] || a.naturaleza_gasto}`}
                          {a.tipo_origen === 'manual' && `Manual: ${usd(a.monto_manual)}`}
                        </td>
                        <td className="py-1.5 text-right whitespace-nowrap">
                          <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Editar" onClick={() => abrirAj(a)}><Pencil className="w-4 h-4" /></button>
                          <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 ml-1" title="Eliminar" onClick={() => eliminarAj.mutate(a.id)}><Trash2 className="w-4 h-4" /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>

            {/* conciliación */}
            <div className="flex items-center justify-between gap-2">
              <h2 className="text-base font-semibold">Conciliación</h2>
              <div className="flex items-end gap-2">
                <label className="flex flex-col text-xs text-muted-foreground">Desde<input type="date" className={`${fieldCls} mt-1`} value={desde} onChange={(e) => setDesde(e.target.value)} /></label>
                <label className="flex flex-col text-xs text-muted-foreground">Hasta<input type="date" className={`${fieldCls} mt-1`} value={hasta} onChange={(e) => setHasta(e.target.value)} /></label>
                <PrintButton />
              </div>
            </div>
            <Imprimible titulo={`MPM · ${sel.etiqueta}`} subtitulo={`Conciliación del ${desde} al ${hasta}`}>
            <Card><CardContent className="overflow-x-auto pt-4">
              {concLoad && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
              {conc && (
                <table className="w-full text-sm"><tbody>
                  <tr className="border-b"><td className="py-2 px-3">{SUBTOTALES[conc.subtotal_base]} (NIIF 18)</td><td className="py-2 px-3 text-right font-medium">{usd(conc.base_valor)}</td></tr>
                  {(conc.ajustes || []).map((a: any, i: number) => (
                    <tr key={i} className="border-b last:border-0"><td className="py-1.5 px-3">{a.signo > 0 ? '(+) ' : '(−) '}{a.concepto}</td><td className="py-1.5 px-3 text-right">{usd(a.valor)}</td></tr>
                  ))}
                  <tr className="border-t-2 border-primary/60 font-bold bg-primary/5"><td className="py-2 px-3">= {sel.etiqueta}</td><td className="py-2 px-3 text-right">{usd(conc.mpm_valor)}</td></tr>
                </tbody></table>
              )}
            </CardContent></Card>
            </Imprimible>
          </div>
        ) : (
          <Card><CardContent className="py-16 text-center text-muted-foreground">Selecciona o crea una medida para ver su conciliación.</CardContent></Card>
        )}
      </div>

      {/* modal definición */}
      {defOpen && (
        <Modal title={defEdit ? 'Editar MPM' : 'Nueva MPM'} onClose={() => setDefOpen(false)}>
          <div className="space-y-3">
            <Campo label="Etiqueta *"><input className={fieldCls} value={def.etiqueta} onChange={(e) => setDef({ ...def, etiqueta: e.target.value })} placeholder="EBITDA ajustado" /></Campo>
            <Campo label="Descripción (qué aspecto del desempeño representa)"><textarea className={fieldCls} rows={2} value={def.descripcion} onChange={(e) => setDef({ ...def, descripcion: e.target.value })} /></Campo>
            <Campo label="Por qué es útil"><textarea className={fieldCls} rows={2} value={def.por_que_util} onChange={(e) => setDef({ ...def, por_que_util: e.target.value })} /></Campo>
            <Campo label="Subtotal NIIF base (más comparable)">
              <select className={fieldCls} value={def.subtotal_base} onChange={(e) => setDef({ ...def, subtotal_base: e.target.value })}>
                {Object.entries(SUBTOTALES).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </Campo>
            <Switch checked={def.activo} onChange={(v) => setDef({ ...def, activo: v })} label="Activa" />
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setDefOpen(false)}>Cancelar</Button>
              <Button isLoading={guardarDef.isPending} disabled={!def.etiqueta.trim()} onClick={() => guardarDef.mutate()}>Guardar</Button>
            </div>
          </div>
        </Modal>
      )}

      {/* modal ajuste */}
      {ajOpen && (
        <Modal title={ajEdit ? 'Editar ajuste' : 'Nuevo ajuste'} onClose={() => setAjOpen(false)}>
          <div className="space-y-3">
            <Campo label="Concepto *"><input className={fieldCls} value={aj.concepto} onChange={(e) => setAj({ ...aj, concepto: e.target.value })} placeholder="Depreciación y amortización" /></Campo>
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Signo"><select className={fieldCls} value={aj.signo} onChange={(e) => setAj({ ...aj, signo: Number(e.target.value) })}><option value={1}>Sumar (+)</option><option value={-1}>Restar (−)</option></select></Campo>
              <Campo label="Origen del monto"><select className={fieldCls} value={aj.tipo_origen} onChange={(e) => setAj({ ...aj, tipo_origen: e.target.value })}><option value="naturaleza">Por naturaleza</option><option value="cuenta">Por cuenta</option><option value="manual">Monto manual</option></select></Campo>
            </div>
            {aj.tipo_origen === 'naturaleza' && (
              <Campo label="Naturaleza"><select className={fieldCls} value={aj.naturaleza_gasto} onChange={(e) => setAj({ ...aj, naturaleza_gasto: e.target.value })}>{Object.entries(NATS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></Campo>
            )}
            {aj.tipo_origen === 'cuenta' && (
              <Campo label="Cuenta"><BuscadorCuenta value={aj.cuenta_id} onChange={(id) => setAj({ ...aj, cuenta_id: id })} tipos={['ingreso', 'costo', 'gasto']} allowClear compact placeholder="— Selecciona —" /></Campo>
            )}
            {aj.tipo_origen === 'manual' && (
              <Campo label="Monto"><input type="number" step="0.01" className={fieldCls} value={aj.monto_manual} onChange={(e) => setAj({ ...aj, monto_manual: e.target.value })} /></Campo>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setAjOpen(false)}>Cancelar</Button>
              <Button isLoading={guardarAj.isPending} disabled={!aj.concepto.trim() || (aj.tipo_origen === 'cuenta' && !aj.cuenta_id)} onClick={() => guardarAj.mutate()}>Guardar</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
